import { useMemo } from "react";
import { BrandMark } from "@/components/common/BrandLogo";
import { AI, Brands, Reveal } from "./slideBits";

/**
 * Slide «Eitt sentralt driftssystem» – minimalistisk: ein tynn ring med åtte fagområde som berre er tekst, hårlinjer
 * inn mot eitt blått nav. Entré: områda glir inn frå utsida (spreidde verktøy → eitt system) medan ringen og linjene
 * teiknar seg; så pustar navet og ei bølgje går ut til alle områda med jamne mellomrom («KI ser heilskapen»).
 * Berre transform/opacity + SVG-strek. Teksten til venstre er tre korte linjer: Svare · Foreslå · Handle.
 */
const NODES = ["Kundar & tilbod", "Riggdesign", "Kapasitet & flåte", "Transport & montasje", "Innkvartering & drift", "Økonomi", "Felt (mobil)", "Søknader & dokument"];
const S = 640, C = S / 2, R = 236, HUB_R = 46, LABEL_GAP = 16;

function HubDiagram({ animate }) {
  const nodes = useMemo(() => NODES.map((label, i) => {
    const a = -Math.PI / 2 + (i * Math.PI * 2) / NODES.length;
    const cos = Math.cos(a), sin = Math.sin(a);
    const x = C + cos * R, y = C + sin * R; // anchor dot on the ring
    // label sits outside the ring, anchored away from the centre
    const lx = x + cos * LABEL_GAP, ly = y + sin * LABEL_GAP;
    const anchor = Math.abs(cos) < 0.3 ? "middle" : cos > 0 ? "start" : "end";
    const dy = Math.abs(cos) < 0.3 ? (sin < 0 ? -2 : 12) : 4.5;
    // spoke from the anchor dot to the hub edge; entrance offset = 46 units further out
    const x1 = C + cos * (HUB_R + 8), y1 = C + sin * (HUB_R + 8);
    return { label, i, x, y, lx, ly, anchor, dy, x1, y1, ox: cos * 46, oy: sin * 46, len: Math.hypot(x1 - x, y1 - y) };
  }), []);
  const orbitLen = 2 * Math.PI * R;
  return (
    <svg viewBox={`0 0 ${S} ${S}`} className="pres-hub" role="img" aria-label="Eitt sentralt driftssystem med åtte fagområde rundt" data-testid="pres-hub" data-animate={animate ? "true" : "false"}>
      {/* orbit – draws itself */}
      <circle cx={C} cy={C} r={R} className="pres-hub-orbit" style={{ "--len": orbitLen }} />
      {/* spokes – draw from the ring in to the hub */}
      {nodes.map((n) => <line key={`l${n.i}`} x1={n.x} y1={n.y} x2={n.x1} y2={n.y1} className="pres-hub-spoke" style={{ "--len": n.len, "--i": n.i }} />)}
      {/* one data point at a time wanders in to the hub */}
      {animate && nodes.map((n) => (
        <circle key={`d${n.i}`} r="3" opacity="0" className="pres-hub-dot">
          <animateMotion dur="2.6s" begin={`${2.4 + n.i * 1.1}s`} repeatCount="indefinite" path={`M ${n.x} ${n.y} L ${n.x1} ${n.y1}`} calcMode="spline" keyTimes="0;1" keySplines="0.5 0 0.2 1" />
          <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.15;0.8;1" dur="2.6s" begin={`${2.4 + n.i * 1.1}s`} repeatCount="indefinite" />
        </circle>
      ))}
      {/* areas: anchor dot + label, gliding in from outside the ring */}
      {nodes.map((n) => (
        <g key={`n${n.i}`} transform={`translate(${n.x} ${n.y})`} data-testid={`pres-hub-node-${n.i}`}>
          <g className="pres-hub-area" style={{ "--i": n.i, "--ox": `${n.ox}px`, "--oy": `${n.oy}px` }}>
            <circle r="3.5" className="pres-hub-anchor" />
            <text x={n.lx - n.x} y={n.ly - n.y + n.dy} textAnchor={n.anchor} className="pres-hub-label">{n.label}</text>
          </g>
        </g>
      ))}
      {/* hub: pulse wave → ring → disc with the mark */}
      <g transform={`translate(${C} ${C})`}>
        <circle r={R} className="pres-hub-wave" />
        <circle r={R} className="pres-hub-wave pres-hub-wave-2" />
        <g className="pres-hub-core">
          <circle r={HUB_R + 10} className="pres-hub-halo" />
          <circle r={HUB_R} className="pres-hub-disc" />
          <g transform={`translate(-19 -19) scale(${38 / S})`}><BrandMark color="#ffffff" accent="#ffffff" /></g>
          <text y={HUB_R + 30} textAnchor="middle" className="pres-hub-core-title">Stord Innkvartering</text>
          <text y={HUB_R + 46} textAnchor="middle" className="pres-hub-core-sub">eitt driftssystem</text>
        </g>
      </g>
    </svg>
  );
}

const KI_CAN = [
  { n: "01", title: "Svare", text: "«Kan vi levere 120 personar på Mongstad frå 1. desember?» – med kjelder frå systemet." },
  { n: "02", title: "Foreslå", text: "Sourcingplan, riggdesign og tilbodstekst – forklart, så du ser kvifor." },
  { n: "03", title: "Handle", text: "Reservere, opprette, varsle – alltid forslag → stadfest → logg." },
];

export default function SystemSlide() {
  const animate = typeof window === "undefined" ? false : !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  return (
    <div className="pres-slide-inner flex flex-col" data-testid="pres-slide-system">
      <Brands />
      <div className="pres-system">
        <div className="pres-system-copy">
          <h2 className="pres-h2 pres-system-title" data-testid="pres-system-title">
            <Reveal i={0}>Eitt sentralt driftssystem</Reveal>
            <Reveal i={1}>som samlar alt</Reveal>
          </h2>
          <p className="pres-lead pres-system-lead pres-in" style={{ "--i": 2 }}>Tilbod, rigg, flåte, transport, innkvartering og økonomi – i same datamodell.</p>
          <ol className="pres-system-list" data-testid="pres-system-list">
            {KI_CAN.map((k, i) => (
              <li key={k.n} className="pres-system-item pres-in" style={{ "--i": 4 + i }} data-testid={`pres-system-item-${i}`}>
                <span className="pres-system-item-n">{k.n}</span>
                <div className="pres-system-item-body">
                  <div className="pres-system-item-title"><span className="pres-accent-ki">{AI}</span> kan {k.title.toLowerCase()}</div>
                  <div className="pres-system-item-text">{k.text}</div>
                </div>
              </li>
            ))}
          </ol>
          <div className="pres-system-foot pres-in" style={{ "--i": 8 }} data-testid="pres-system-foot">Motoren reknar. <span className="pres-accent-ki">{AI}</span> forklarer. Du stadfestar.</div>
        </div>
        <div className="pres-system-figure">
          <HubDiagram animate={animate} />
        </div>
      </div>
    </div>
  );
}
